import { Axios } from "api/axios";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MasterForm from "./MasterFrom";

const EnquiryCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  // Get current user from Redux store
  const user = useSelector((state: any) => state.Login.user);
  const userId = user?.user_id || user?.id || null;

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    try {
      // Prepare enquiry items - item_file will be handled separately in FormData (files can't be JSON stringified)
      const enquiryItems = formData.items.map((item: any) => {
        return {
          category_id: item.category_id || null,
          category_name: item.category || "",
          sub_category_id: item.sub_category_id || null,
          sub_category_name: item.sub_category || "",
          item_id: item.item_id || null,
          item_name: item.item_name || "",
          item_code: item.item_code || "",
          item_details: item.item_details || null,
          // Denormalized HSN and GST fields
          hsn_code_id: item.hsn_code_id || null,
          hsn_code_name: item.hsn_code_name || "",
          gst_id: item.gst_id || null,
          gst_percent: item.gst_percent || null,
          // Denormalized unit fields
          unit_id: item.unit_id || null,
          unit_name: item.unit || "",
          // Selling price (not shown in UI but required in payload)
          selling_price: item.selling_price || null,
          length: item.length || null,
          width: item.width || null,
          height: item.height || null,
          required_quantity: item.required_quantity || "",
          remarks: item.remarks || null,
          // item_file will be handled separately in FormData (can't be JSON stringified)
        };
      });

      // Prepare customer details object
      const customerDetails = {
        ...formData?.customer_details,
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name || "",
        contact_person_name: formData.contact_person_name || "",
        contact_person_phone_no: formData.contact_person_phone_no || "",
      };

      // Prepare site visit object (single object, not array - based on backend)
      let siteVisit = null;

      // // Debug: Log formData to see what we're receiving
      // console.log("=== Site Visit Debug ===");
      // console.log("site_visit_enabled:", formData.site_visit_enabled);
      // console.log("site_visits:", formData.site_visits);
      // console.log("site_visits length:", formData.site_visits?.length);

      // If site visit is enabled, we must have site visit data
      if (formData.site_visit_enabled) {
        if (formData.site_visits && formData.site_visits.length > 0) {
          // Take first site visit (backend expects single object)
          const siteVisitData = formData.site_visits[0];
          console.log("Site Visit Data:", siteVisitData);

          // Prepare site visit object with all required fields
          siteVisit = {
            staff_id: siteVisitData.staff_id || null,
            staff_details: {
              user_id: siteVisitData.staff_id || null,
              staff_name: siteVisitData.staff_name || "",
            },
            // Denormalized staff fields
            staff_name: siteVisitData.staff_name || null,
            staff_country_code: siteVisitData.staff_country_code || null,
            staff_contact_number: siteVisitData.staff_contact_number || null,
            staff_email: siteVisitData.staff_email || null,
            site_visit_date:
              siteVisitData.site_visit_date || moment().format("YYYY-MM-DD"),
            location_url: siteVisitData.location_url || null,
            address: siteVisitData.address || null,
            city: siteVisitData.city || null,
            state: siteVisitData.state || null,
            remarks: siteVisitData.remarks || null,
          };
          console.log("Prepared Site Visit Object:", siteVisit);
        } else {
          // If enabled but no data, this should have been caught by validation
          // But we'll log a warning and continue (validation should prevent this)
          console.warn(
            "Site visit is enabled but no site visit data found in array"
          );
          toast.error(
            "Site visit is enabled but no site visit data provided. Please add a site visit."
          );
          throw new Error(
            "Site visit is enabled but no site visit data provided"
          );
        }
      } else {
        console.log("Site visit is disabled");
      }
      console.log("========================\n");

      // Create the main payload object matching backend structure
      const payload: any = {
        enquiry_date: formData.enquiry_date || moment().format("YYYY-MM-DD"),
        enquiry_source: formData.source_of_enquiry || null,
        remark: formData.remark || null,
        customer_details: customerDetails,
        site_visit_required: formData.site_visit_enabled || false,
        enquiry_items: enquiryItems,
      };

      // Add site visit if provided
      if (siteVisit) {
        payload.site_visit = siteVisit;
      }

      // Add user IDs if available
      if (userId) {
        payload.created_by = userId;
        payload.updated_by = userId;
      }

      // Create FormData - Backend expects individual fields, not wrapped in "data"
      const formDataPayload = new FormData();

      // Append individual fields as the backend expects them
      formDataPayload.append("enquiry_date", payload.enquiry_date);
      if (payload.enquiry_source) {
        formDataPayload.append("enquiry_source", payload.enquiry_source);
      }
      if (payload.remark) {
        formDataPayload.append("remark", payload.remark);
      }

      // Append JSON fields as strings (backend will parse them)
      formDataPayload.append(
        "customer_details",
        JSON.stringify(customerDetails)
      );
      formDataPayload.append(
        "site_visit_required",
        payload.site_visit_required ? "true" : "false"
      );
      formDataPayload.append("enquiry_items", JSON.stringify(enquiryItems));

      // Always append site_visit if site_visit_required is true
      // Even if it's null, we should send it to match backend expectations
      if (payload.site_visit_required) {
        if (siteVisit) {
          formDataPayload.append("site_visit", JSON.stringify(siteVisit));
          console.log("Site visit appended to FormData:", siteVisit);
        } else {
          // If site visit is required but we don't have data, this is an error
          console.error("Site visit is required but siteVisit object is null");
          toast.error("Site visit data is missing. Please add a site visit.");
          throw new Error("Site visit is required but data is missing");
        }
      }

      // Append files for each item
      // Each item has a single file in item_file field
      formData.items.forEach((item: any, itemIndex: number) => {
        if (item.item_file && item.item_file instanceof File) {
          // Append single file with field name matching backend expectation
          formDataPayload.append(
            `enquiry_items[${itemIndex}][item_file]`,
            item.item_file
          );
        }
      });

      // Log FormData contents for debugging
      console.log("=== FormData Payload ===");
      console.log("JSON Payload:", JSON.stringify(payload, null, 2));
      console.log("\nFormData Entries:");
      formDataPayload.forEach((value, key) => {
        if (value instanceof File) {
          console.log(
            `${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`
          );
        } else {
          console.log(`${key}:`, value);
        }
      });
      console.log("========================\n");

      // Send FormData
      const response = await Axios.post("/enquiry/create/", formDataPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        toast.success("Enquiry created successfully!");
        // Only navigate on success, not on error
        navigate("/enquiry", { state: { clearState: true } });
        return; // Explicit return to prevent any further execution
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to create enquiry";
      toast.error(errorMessage);
      // Don't re-throw the error - it's already handled and displayed
      // Re-throwing causes form to refresh/redirect
      console.error("Enquiry creation error:", error);
    }
  };

  return (
    <MasterForm isEdit={false} onSubmit={handleSubmit} loading={loading} />
  );
};

export default EnquiryCreateForm;
